import * as React from "react";
import { CircleOff, Flower2, Leaf, Moon, Snowflake, Sun, Sunrise, Sunset } from "lucide-react";
import {
  FACET_TITLES,
  SOURCE_LABELS,
  SOURCE_TITLE,
  TIER_LABELS,
  TIER_TITLE,
} from "@/components/Backgrounds/facetLabels";
import { FiltersPopoverLayout } from "@/components/Filters/FiltersPopoverLayout";
import { Separator } from "@/components/ui";
import { cn } from "@/lib/utils";
import {
  BACKGROUND_FACETS,
  BackgroundFacet,
  BackgroundSource,
  BackgroundTier,
  BackgroundVisibility,
} from "@/services/bedrock/dtos/BackgroundAsset";
import { ExpandableTiles, TileOption } from "./ExpandableTiles";
import { FacetPills, PillOption } from "./FacetPills";
import {
  clearPopoverFilters,
  activeFilterCount,
  facetValueLabel,
  FLAG_WORDS,
  FLAGS,
  LibraryFilters,
  LibraryFlag,
  LibraryMode,
} from "./libraryFilterParams";

const COLLAPSED_PILLS = 6;

const SectionLabel: React.FC<React.PropsWithChildren> = ({ children }) => (
  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{children}</span>
);

const Section: React.FC<React.PropsWithChildren<{ title: string }>> = ({ title, children }) => (
  <div className="flex flex-col gap-2">
    <SectionLabel>{title}</SectionLabel>
    {children}
  </div>
);

const TIME_ICONS: Record<string, React.ReactNode> = {
  morning: <Sunrise className="size-4" />,
  midday: <Sun className="size-4" />,
  golden_hour: <Sunset className="size-4" />,
  night: <Moon className="size-4" />,
  none: <CircleOff className="size-4" />,
};
const SEASON_ICONS: Record<string, React.ReactNode> = {
  spring: <Flower2 className="size-4" />,
  summer: <Sun className="size-4" />,
  fall: <Leaf className="size-4" />,
  winter: <Snowflake className="size-4" />,
  none: <CircleOff className="size-4" />,
};

const PALETTE_SWATCHES: Record<string, string> = {
  light: "bg-white",
  dark: "bg-slate-800",
  neutral: "bg-[#d6cfc4]",
  colorful: "bg-[conic-gradient(#f43f5e,#f59e0b,#10b981,#3b82f6,#a855f7,#f43f5e)]",
};

/** A tiny frame with the open area shaded, so "Top" reads as "room at the top". */
const SpaceFrame: React.FC<{ space: string }> = ({ space }) => (
  <span className="relative block h-5 w-4 overflow-hidden rounded-[3px] border-[1.5px] border-current">
    <span
      className={cn(
        "absolute bg-current opacity-35",
        space === "top" && "inset-x-0 top-0 h-1/2",
        space === "bottom" && "inset-x-0 bottom-0 h-1/2",
        space === "left" && "inset-y-0 left-0 w-1/2",
        space === "right" && "inset-y-0 right-0 w-1/2",
        space === "center" && "inset-[25%] rounded-[1px]",
        space === "none" && "hidden"
      )}
    />
  </span>
);

function facetValues(facet: BackgroundFacet, mode: LibraryMode): string[] {
  // "No particular season" is a tagging state, not something a coach looks for.
  return (BACKGROUND_FACETS[facet] as readonly string[]).filter(value => mode === "admin" || value !== "none");
}

const pillOptions = (facet: BackgroundFacet, mode: LibraryMode): PillOption[] =>
  facetValues(facet, mode).map(value => ({
    value,
    label: facetValueLabel(facet, value),
    swatch:
      facet === "palette" && PALETTE_SWATCHES[value] ? (
        <span
          className={cn("size-3 shrink-0 rounded-full ring-1 ring-slate-300", PALETTE_SWATCHES[value])}
          aria-hidden="true"
        />
      ) : undefined,
  }));

const tileOptions = (
  facet: BackgroundFacet,
  mode: LibraryMode,
  icon: (value: string) => React.ReactNode
): TileOption[] =>
  facetValues(facet, mode).map(value => ({ value, label: facetValueLabel(facet, value), icon: icon(value) }));

type FacetPicker = "pills" | "tiles";
const FACET_ORDER: { facet: BackgroundFacet; picker: FacetPicker; collapse?: boolean }[] = [
  { facet: "setting", picker: "pills", collapse: true },
  { facet: "mood", picker: "pills" },
  { facet: "timeOfDay", picker: "tiles" },
  { facet: "season", picker: "tiles" },
  { facet: "region", picker: "pills", collapse: true },
  { facet: "occasion", picker: "pills", collapse: true },
  { facet: "palette", picker: "pills" },
  { facet: "emptySpace", picker: "tiles" },
];

const TILE_ICONS: Partial<Record<BackgroundFacet, (value: string) => React.ReactNode>> = {
  timeOfDay: value => TIME_ICONS[value],
  season: value => SEASON_ICONS[value],
  emptySpace: value => <SpaceFrame space={value} />,
};

const FlagChoice: React.FC<{
  flag: LibraryFlag;
  value: boolean | undefined;
  onChange(value: boolean | undefined): void;
}> = ({ flag, value, onChange }) => {
  const word = FLAG_WORDS[flag];
  const choices: { label: string; value: boolean | undefined }[] = [
    { label: "Any", value: undefined },
    { label: "With", value: true },
    { label: "Without", value: false },
  ];
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-slate-700 first-letter:uppercase">{word}</span>
      <div className="flex rounded-lg border border-slate-200 p-0.5" role="group" aria-label={word}>
        {choices.map(choice => {
          const selected = value === choice.value;
          return (
            <button
              key={choice.label}
              type="button"
              aria-pressed={selected}
              onClick={() => onChange(choice.value)}
              className={cn(
                "h-6 rounded-md px-2.5 text-xs font-medium transition-colors cursor-pointer",
                selected ? "bg-slate-100 text-slate-900" : "text-slate-500 hover:text-slate-800"
              )}
            >
              {choice.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const VISIBILITY_OPTIONS: PillOption[] = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" },
];
const SOURCE_OPTIONS: PillOption[] = (Object.keys(SOURCE_LABELS) as BackgroundSource[]).map(value => ({
  value,
  label: SOURCE_LABELS[value],
}));
const TIER_OPTIONS: PillOption[] = (Object.keys(TIER_LABELS) as BackgroundTier[]).map(value => ({
  value,
  label: TIER_LABELS[value],
}));

export function resultLabel(count: number | null | undefined): string | null | undefined {
  if (count == null) return count;
  return count === 1 ? "1 background" : `${count.toLocaleString()} backgrounds`;
}

export interface LibraryFiltersPopoverProps {
  mode: LibraryMode;
  filters: LibraryFilters;
  onFiltersChange(filters: LibraryFilters): void;
  /** The filtered total; null while it loads. */
  resultCount?: number | null;
  onDone(): void;
  /** Shown at the top on phones, where the toolbar has no room for them. */
  view?: React.ReactNode;
}

/** Every filter beyond search, in short labeled sections. Changes apply as they're clicked. */
export const LibraryFiltersPopover: React.FC<LibraryFiltersPopoverProps> = ({
  mode,
  filters,
  onFiltersChange,
  resultCount,
  onDone,
  view,
}) => {
  const setFacet = (facet: BackgroundFacet, values: string[]) =>
    onFiltersChange({ ...filters, facets: { ...filters.facets, [facet]: values } });
  const setFlag = (flag: LibraryFlag, value: boolean | undefined) => {
    const flags = { ...filters.flags };
    if (value === undefined) delete flags[flag];
    else flags[flag] = value;
    onFiltersChange({ ...filters, flags });
  };

  return (
    <FiltersPopoverLayout
      side="bottom"
      align="end"
      initialFocus={false}
      onClear={
        activeFilterCount(filters, mode) > 0 ? () => onFiltersChange(clearPopoverFilters(filters, mode)) : undefined
      }
      onDone={onDone}
      resultLabel={resultLabel(resultCount)}
    >
      {view && (
        <div className="flex flex-col gap-2 sm:hidden">
          <SectionLabel>View</SectionLabel>
          {view}
        </div>
      )}

      {FACET_ORDER.map(({ facet, picker, collapse }) => (
        <Section key={facet} title={FACET_TITLES[facet]}>
          {picker === "tiles" ? (
            <ExpandableTiles
              label={FACET_TITLES[facet]}
              options={tileOptions(facet, mode, TILE_ICONS[facet]!)}
              values={filters.facets[facet] ?? []}
              onChange={values => setFacet(facet, values)}
            />
          ) : (
            <FacetPills
              label={FACET_TITLES[facet]}
              options={pillOptions(facet, mode)}
              values={filters.facets[facet] ?? []}
              onChange={values => setFacet(facet, values)}
              collapsedCount={collapse ? COLLAPSED_PILLS : undefined}
            />
          )}
        </Section>
      ))}

      {mode === "coach" ? (
        <Section title="Leave out">
          <FacetPills
            label="Leave out"
            options={FLAGS.map(flag => ({
              value: flag,
              label: FLAG_WORDS[flag].charAt(0).toUpperCase() + FLAG_WORDS[flag].slice(1),
            }))}
            values={FLAGS.filter(flag => filters.flags[flag] === false)}
            onChange={values => {
              const flags: LibraryFilters["flags"] = {};
              (values as LibraryFlag[]).forEach(flag => (flags[flag] = false));
              onFiltersChange({ ...filters, flags });
            }}
          />
        </Section>
      ) : (
        <>
          <Section title="Contains">
            <div className="flex flex-col gap-2">
              {FLAGS.map(flag => (
                <FlagChoice key={flag} flag={flag} value={filters.flags[flag]} onChange={v => setFlag(flag, v)} />
              ))}
            </div>
          </Section>
          {/* Admin-only filters, set apart from what a photo shows. */}
          <div className="flex items-center gap-3 pt-1">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Curation</span>
            <Separator className="flex-1" />
          </div>
          <Section title="Visibility">
            <FacetPills
              label="Visibility"
              options={VISIBILITY_OPTIONS}
              values={filters.visibility}
              onChange={values => onFiltersChange({ ...filters, visibility: values as BackgroundVisibility[] })}
              required
            />
          </Section>
          <Section title={SOURCE_TITLE}>
            <FacetPills
              label={SOURCE_TITLE}
              options={SOURCE_OPTIONS}
              values={filters.source}
              onChange={values => onFiltersChange({ ...filters, source: values as BackgroundSource[] })}
            />
          </Section>
          <Section title={TIER_TITLE}>
            <FacetPills
              label={TIER_TITLE}
              options={TIER_OPTIONS}
              values={filters.tier}
              onChange={values => onFiltersChange({ ...filters, tier: values as BackgroundTier[] })}
            />
          </Section>
        </>
      )}
    </FiltersPopoverLayout>
  );
};
